import * as React from 'react';
import { useState } from 'react';
import { X, Trophy, Target, Clock, Gift, Users, Info } from 'lucide-react';
import type { Tournament } from '../../../types/features';
import { tournamentService } from '../../../features/tournaments/tournamentService';
import { playerService } from '../../../features/player/playerService';

interface TournamentDetailsModalProps {
    tournament: Tournament;
    onClose: () => void;
}

export const TournamentDetailsModal: React.FC<TournamentDetailsModalProps> = ({
    tournament,
    onClose,
}) => {
    const [isJoining, setIsJoining] = useState(false);
    const player = playerService.getProfile();
    const isParticipant = tournament.participants.some(p => p.id === player.id);

    const handleJoinTournament = () => {
        setIsJoining(true);
        try {
            const joined = tournamentService.joinTournament(tournament.id, player.id);
            if (joined) {
                // TODO: Show success notification
            } else {
                // TODO: Show error notification
            }
        } catch (error) {
            console.error('Failed to join tournament:', error);
            // TODO: Show error notification
        } finally {
            setIsJoining(false);
        }
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatPrize = (position: number) => {
        const prize = tournamentService.calculatePrize(tournament, position);
        return `${prize} coins`;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="relative h-48">
                    <img
                        src={tournament.imageUrl}
                        alt={tournament.name}
                        className="w-full h-full object-cover"
                    />
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-1 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70"
                    >
                        <X className="w-6 h-6" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                        <h2 className="text-2xl font-bold text-white">
                            {tournament.name}
                        </h2>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Description */}
                    <p className="text-gray-600">{tournament.description}</p>

                    {/* Key Info */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center text-gray-600">
                            <Clock className="w-5 h-5 mr-2" />
                            <div>
                                <div className="text-sm font-medium">Start</div>
                                <div>{formatDate(tournament.startDate)}</div>
                            </div>
                        </div>
                        <div className="flex items-center text-gray-600">
                            <Clock className="w-5 h-5 mr-2" />
                            <div>
                                <div className="text-sm font-medium">End</div>
                                <div>{formatDate(tournament.endDate)}</div>
                            </div>
                        </div>
                        <div className="flex items-center text-gray-600">
                            <Gift className="w-5 h-5 mr-2" />
                            <div>
                                <div className="text-sm font-medium">Prize Pool</div>
                                <div>{tournament.prizePool} coins</div>
                            </div>
                        </div>
                        <div className="flex items-center text-gray-600">
                            <Users className="w-5 h-5 mr-2" />
                            <div>
                                <div className="text-sm font-medium">Participants</div>
                                <div>
                                    {tournament.participants.length}
                                    {tournament.maxParticipants
                                        ? `/${tournament.maxParticipants}`
                                        : ''}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rules */}
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Tournament Rules</h3>
                        <div className="space-y-2 text-gray-600">
                            <div className="flex items-center">
                                <Info className="w-4 h-4 mr-2" />
                                <span>Entry Fee: {tournament.entryFee} coins</span>
                            </div>
                            {tournament.settings.minBet && (
                                <div className="flex items-center">
                                    <Info className="w-4 h-4 mr-2" />
                                    <span>Minimum Bet: {tournament.settings.minBet} coins</span>
                                </div>
                            )}
                            {tournament.settings.spinLimit && (
                                <div className="flex items-center">
                                    <Info className="w-4 h-4 mr-2" />
                                    <span>Spin Limit: {tournament.settings.spinLimit} spins</span>
                                </div>
                            )}
                            <div className="flex items-center">
                                <Info className="w-4 h-4 mr-2" />
                                <span>
                                    Bonus Spins: {tournament.settings.includeBonusSpins ? 'Included' : 'Not Included'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Prizes */}
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Prizes</h3>
                        <div className="space-y-2">
                            {tournament.prizes.map((prize) => (
                                <div
                                    key={`${prize.position}-${prize.positionEnd || prize.position}`}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                                >
                                    <span className="font-medium">
                                        {prize.positionEnd
                                            ? `${prize.position}-${prize.positionEnd} place`
                                            : `${prize.position} place`}
                                    </span>
                                    <span className="text-primary">
                                        {prize.isPercentage
                                            ? `${prize.amount}% of pool`
                                            : formatPrize(prize.position)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Join Button */}
                    {tournament.status === 'active' && !isParticipant && (
                        <button
                            onClick={handleJoinTournament}
                            disabled={isJoining}
                            className="w-full py-3 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isJoining ? 'Joining...' : 'Join Tournament'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}; 